import { useCallback, useRef, useState } from 'react';
import { Clipboard, LayoutAnimation } from 'react-native';
import { ActionSheetRow } from '@shared/components/ActionSheet';
import { AnchoredView } from '@shared/components/AnchoredSheet';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBReactionButtonWithPicker } from '@shared/scenes/chat/thread/components/reaction/buttonWithPicker';
import { MBInvisibleReactionPicker } from '@shared/scenes/chat/thread/containers/messageInvisibleReactionPicker';
import { MBMessageReactionsAndSeenBy } from '@shared/scenes/chat/thread/containers/messageReactionsAndSeenBy';
import { API } from '@shared/types/api';
import { MBAlert } from '@shared/util/alert';
import { MBAnalytics } from '@shared/util/analytics';
import { useBoolean } from '@shared/util/hooks';
import { MBLinking } from '@shared/util/linking';
import { MBMessageDeliveryStatus } from '../deliveryStatus';
import { MBUrgentBadge } from '../urgentBadge';
import styles from './LongPressMenu.module.css';
import { MBMessageBubbleAttachments } from './components/attachments';
import { MBMessageBubbleContent } from './components/content';
import { MBSenderImage } from './components/senderImage';
import { MBSenderName } from './components/senderName';
import { MBSentBySMS } from './components/sentBySMS';
import { Styles, UnvalidatedStyles } from './styles';

export interface MBMessageBubbleProps {
  message: API.ChatMessageFragment;
  layout: MBChatMessageLayoutOptions;
  theme: MBMessageBubbleTheme;
  deliveryStatus?: MBDeliveryStatus;

  /*
        Used by reaction buttons etc to determine if should call mutation
        for channel message or thread comment.
        TODO(fant): remove this when chat messages have been unified.
    */
  channelSlug: string;

  /*
        Callback for when message bubble is pressed. If this is not set,
        the default press action will be showing message details (aka time sent)
    */
  onPress?: VoidFunction;

  /*
        Callback for when the user wants to remove the message. When this
        prop is sent "Remove Message" will be shown on long press. So this
        property should not be set when the user cannot remove (eg for
        messages sent by other users).
    */
  onDelete?: VoidFunction;

  /*
        Callback for when the user wants to edit the message
    */
  onEdit?: VoidFunction;

  /*
        TODO(fant): when searching and selecting a message, we need to scroll
        to it in a chat thread. This does not work well together with link
        previews since they're loaded async and push around the entire content.
        For now, we solve this by simply hiding link previews when showing
        search results.
    */
  isHidingLinkPreview?: boolean;

  isDesktop: boolean;
  marginLeft?: number;
}

export function MBMessageBubble({
  message,
  layout,
  theme,
  deliveryStatus,
  channelSlug,
  onPress: onPressProp,
  onDelete,
  onEdit,
  isHidingLinkPreview,
  isDesktop,
}: MBMessageBubbleProps) {
  const [isShowingDetails, setIsShowingDetails] = useState(false);
  const [isHovered, onHoverStart, onHoverEnd] = useBoolean(!isDesktop);
  const [showLongPressMenu, openLongPressMenu, closeLongPressMenu] = useBoolean();
  const [showLinkPreviewMenu, openLinkPreviewMenu, closeLinkPreviewMenu] = useBoolean();
  const [lastPressedUrl, setLastPressedUrl] = useState<string>('');
  const reactionPickerRef = useRef<MBReactionButtonWithPicker | null>(null);
  const anchorName = `--message-bubble-${message.id}`;

  const isSent = !deliveryStatus || deliveryStatus === 'sent';

  const isShowingSenderName = isShowingDetails || (!layout.isGroupedWithAbove && isSent);

  const toggleDetails = useCallback(() => {
    const willShowDetails = !isShowingDetails;
    setIsShowingDetails(willShowDetails);
    if (!!LayoutAnimation) {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
    if (willShowDetails) {
      MBAnalytics.track('Message::ShowDetails', { messageId: message.id });
    }
  }, [isShowingDetails, message.id]);

  const confirmDelete = useCallback(
    () =>
      MBAlert.confirm({
        title: 'Delete message',
        message: 'Are you sure you want to delete this message? This action cannot be undone.',
        confirmText: 'Yes, delete',
        confirmStyle: 'destructive',
        onConfirm: onDelete,
      }),
    [onDelete],
  );

  const onPress = useCallback(() => {
    if (onPressProp) {
      onPressProp();
    } else {
      toggleDetails();
    }
  }, [onPressProp, toggleDetails]);

  const onLongPress = useCallback(() => {
    openLongPressMenu();
  }, [openLongPressMenu]);

  const onLongPressLinkPreview = useCallback(
    (url: string) => {
      setLastPressedUrl(url);
      openLinkPreviewMenu();
    },
    [openLinkPreviewMenu],
  );

  const longPressOptions = [
    {
      title: 'Add a Reaction...',
      hidden: !isSent,
      onPress: () => reactionPickerRef.current?.showPicker(),
    },
    {
      title: 'Copy Text',
      hidden: !message.content,
      onPress: () => {
        Clipboard.setString(message.content);
        MBAlert.alert({ message: 'Copied to clipboard' });
      },
    },
    {
      title: 'Edit Message',
      hidden: !message.sender.isMe || !message.content.length,
      onPress: onEdit,
    },
    {
      title: 'Delete Message',
      hidden: !onDelete || !message.sender.isMe,
      destructive: true,
      onPress: confirmDelete,
    },
  ];

  const linkPreviewOptions = [
    {
      title: 'Visit Link',
      onPress: () => MBLinking.open(lastPressedUrl),
    },
    {
      title: 'Add a Reaction...',
      hidden: !isSent,
      onPress: () => reactionPickerRef.current?.showPicker(),
    },
    {
      title: 'Edit Message',
      hidden: !onEdit,
      onPress: () => onEdit?.(),
    },
    {
      title: 'Delete Message',
      hidden: !onDelete,
      destructive: true,
      onPress: confirmDelete,
    },
  ];

  const content = (
    <>
      {/*
          This is somewhat of a hack that needs explaining... To easily show the
          reaction picker when pressing "Add Reaction..." on message long press,
          we want to reuse our picker logic from reaction/buttonWithPicker to
          not have to copy code. To easily do that, we have this thin component
          that is basically an invisible MBReactionButtonWithPicker. When we
          want to add a reaction from an MBActionSheet, we simply get the ref
          of this component and call showPicker. Somewhat hacky, but fairly clean.
        */}
      <MBInvisibleReactionPicker
        ref={ref => {
          reactionPickerRef.current = ref;
        }}
        anchorName={anchorName}
        message={message}
        channelSlug={channelSlug}
      />

      {isShowingSenderName && (
        <MBSenderName
          name={message.sender.isMe ? undefined : message.sender.fullName}
          align={theme.align}
          imagePlaceholder={theme.showImage}
          date={message.createdAt}
        />
      )}

      {/* render the content containing bubble and attachment */}
      <MBView
        style={[
          theme.align === 'left' && Styles.rowAlignLeft,
          theme.align === 'right' && Styles.rowAlignRight,
        ]}
      >
        {theme.showImage && <MBSenderImage message={message} layout={layout} />}

        <View fill>
          {/* 1. at the very top, render the urgent badge */}
          {!!message.urgent && <MBUrgentBadge align={theme.align} />}

          {/* 2. then render the message bubble and its content */}
          <MBHover {...{ onHoverStart, onHoverEnd }}>
            <MBMessageBubbleContent
              onLongPressLinkPreview={onLongPressLinkPreview}
              theme={theme}
              message={message}
              layout={layout}
              channelSlug={channelSlug}
              isHidingLinkPreview={isHidingLinkPreview}
              hovering={isHovered}
              isFirstMessageInGroup={isShowingSenderName}
              anchorName={anchorName}
            />
          </MBHover>

          {/* 3. all attachments in a long list */}
          <MBMessageBubbleAttachments
            message={message}
            align={theme.align}
            channelSlug={channelSlug}
            isSent={isSent}
            onLongPress={onLongPress}
            blink={!!theme.blinkBackgroundColor}
            isFirstMessageInGroup={isShowingSenderName}
          />
        </View>
      </MBView>

      {/*
                    Note: the following elements are rendered below the actual message content.
                    Otherwise the sender image would be pushed up to not be bottom aligned with
                    the message bubble.
                */}
      <View row>
        {/*
                        Add a placeholder for the image so that left aligned content
                        (eg sent by) is aligned with the message bubble
                    */}
        {theme.showImage && <MBView style={Styles.userImagePlaceholder} />}

        <MBView style={[Styles.flex, theme.align === 'right' && Styles.alignRight]}>
          {/* 4. if the message has been sent by SMS or as urgent, show that */}
          {isSent && <MBSentBySMS message={message} />}

          {/*
                            5. a row of reactions and the seen by indicator
                            Note: hiding seen by in grouped messages will
                            make the user miss some seen bys
                        */}
          <MBMessageReactionsAndSeenBy
            message={message}
            layout={layout}
            align={theme.align}
            isDM={theme.isDM}
            pickerBottomMargin={UnvalidatedStyles.reactionPickerBottomMargin}
            channelSlug={channelSlug}
          />
        </MBView>
      </View>

      {/*
                    Lastly, if we have a delivery status (only relevant for
                    the blue me bubble), render that status.
                    Note: don't show this if the message is already seen by someone
                    Note: hiding seen by in grouped messages will make
                        the user miss some seen bys
                */}
      {!!deliveryStatus && !layout.seenByUsers.length && (
        <MBMessageDeliveryStatus
          status={deliveryStatus}
          isEdited={!!message.editedAt}
          style={Styles.deliveryStatus}
          testID="chat-message-delivered"
        />
      )}

      {showLongPressMenu && (
        <AnchoredView
          anchorName={anchorName}
          onClose={closeLongPressMenu}
          className={styles.longPressMenu}
        >
          {longPressOptions
            .filter(option => !option.hidden)
            .map((option, index) => (
              <ActionSheetRow
                key={index}
                option={option}
                onClose={closeLongPressMenu}
                className={styles.longPressMenuRow}
              />
            ))}
        </AnchoredView>
      )}
      {showLinkPreviewMenu && (
        <AnchoredView anchorName={anchorName} onClose={closeLinkPreviewMenu}>
          {linkPreviewOptions
            .filter(option => !option.hidden)
            .map((option, index) => (
              <ActionSheetRow key={index} option={option} onClose={closeLinkPreviewMenu} />
            ))}
        </AnchoredView>
      )}
    </>
  );

  if (isDesktop) {
    return <>{content}</>;
  }

  return (
    <MBTouchableOpacity activeOpacity={0.7} onPress={onPress} onLongPress={onLongPress}>
      {content}
    </MBTouchableOpacity>
  );
}
